import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { X, AlertOctagon, ShieldAlert, HardHat, HeartPulse, Atom, Activity, FileDown, Pill } from "lucide-react";
import { CATEGORY_BG, CATEGORY_LABELS, HAZARD_BG, HAZARD_LABELS } from "../lib/categories";
import { buildElementSheet } from "../lib/pdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ElementDetailDialog = ({ element, open, onOpenChange }) => {
  const [isotopes, setIsotopes] = useState([]);
  const [extra, setExtra] = useState({});
  const [antidote, setAntidote] = useState("");

  useEffect(() => {
    if (!element) { return; }
    let cancelled = false;
    axios.get(`${API}/elements/${element.n}`)
      .then((r) => {
        if (cancelled) return;
        setIsotopes(r.data.isotopes || []);
        setAntidote(r.data.antidote || "");
        setExtra({
          description: r.data.description,
          uses: r.data.uses,
          exposure: r.data.exposure,
          workplace_limit: r.data.workplace_limit,
          discovered: r.data.discovered,
        });
      })
      .catch(() => { if (!cancelled) { setIsotopes([]); setExtra({}); setAntidote(""); } });
    return () => { cancelled = true; };
  }, [element]);

  if (!element) return null;
  const colorCls = CATEGORY_BG[element.cat] || "bg-zinc-200 text-black";
  const hazardCls = HAZARD_BG[element.hazard] || "bg-zinc-300 text-black";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="element-detail-dialog"
        className="max-w-5xl p-0 border-2 border-zinc-950 rounded-none bg-white max-h-[90vh] overflow-y-auto"
      >
        <DialogTitle className="sr-only">{element.name} ({element.s}) — Health & Safety</DialogTitle>
        <DialogDescription className="sr-only">
          Element {element.n} {element.name}. Hazard level: {HAZARD_LABELS[element.hazard]}.
        </DialogDescription>

        {/* Header */}
        <div className="relative border-b-2 border-zinc-950">
          <div className="grid grid-cols-1 md:grid-cols-12">
            <div className={`md:col-span-4 ${colorCls} border-r-0 md:border-r-2 border-zinc-950 p-8 flex flex-col justify-center items-center`}>
              <div className="font-mono text-xs uppercase tracking-[0.3em] mb-2">№ {element.n}</div>
              <div className="font-heading font-black text-7xl md:text-8xl leading-none tracking-tighter">{element.s}</div>
              <div className="font-mono text-sm mt-3 uppercase tracking-widest">{element.name}</div>
              <div className="font-mono text-xs mt-2 opacity-80">{element.mass} u</div>
            </div>
            <div className="md:col-span-8 p-6 md:p-8 flex flex-col justify-between gap-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-1">Category</div>
                  <div className="font-heading font-bold text-xl uppercase tracking-tight">
                    {CATEGORY_LABELS[element.cat]}
                  </div>
                </div>
                <button
                  data-testid="element-detail-close"
                  onClick={() => onOpenChange(false)}
                  className="border-2 border-zinc-950 p-1.5 hover:bg-zinc-950 hover:text-white transition-colors"
                >
                  <X size={18} strokeWidth={2.5} />
                </button>
              </div>

              <div className={`${hazardCls} border-2 border-zinc-950 p-4 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <AlertOctagon size={28} strokeWidth={2.75} />
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] opacity-80">Hazard Level</div>
                    <div className="font-heading font-black text-2xl tracking-tight">{HAZARD_LABELS[element.hazard]}</div>
                  </div>
                </div>
                <div className="font-mono text-xs uppercase tracking-widest hidden sm:flex flex-col items-end">
                  <span>Period {element.period}</span>
                  <span>Group {element.group}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overview / Reference */}
        {extra.description && (
          <div className="border-b-2 border-zinc-950 p-6 md:p-8 bg-zinc-50" data-testid="element-detail-overview">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-zinc-950 bg-white text-zinc-950 font-mono text-[10px] uppercase tracking-[0.25em] font-bold mb-4">
              <Atom size={14} strokeWidth={2.5} /> Overview
            </div>
            <p className="text-base leading-relaxed text-zinc-900 mb-5">{extra.description}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-950 border-2 border-zinc-950">
              {extra.uses && (
                <InfoCell label="Common Uses" value={extra.uses} />
              )}
              {extra.exposure && (
                <InfoCell label="Exposure Routes" value={extra.exposure} />
              )}
              {extra.workplace_limit && (
                <InfoCell label="Workplace Limit" value={extra.workplace_limit} mono />
              )}
              {extra.discovered && (
                <InfoCell label="Discovered" value={extra.discovered} mono />
              )}
            </div>
          </div>
        )}

        {/* Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 border-b-2 border-zinc-950">
          <Section testid="element-detail-symptoms" icon={<HeartPulse strokeWidth={2.5} />}
            label="Health Symptoms" tone="bg-[#FFD700]" content={element.symptoms} borderRight />
          <Section testid="element-detail-complications" icon={<AlertOctagon strokeWidth={2.5} />}
            label="Complications" tone="bg-[#FF8A00]" content={element.complications} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 border-b-2 border-zinc-950">
          <Section testid="element-detail-precautions" icon={<HardHat strokeWidth={2.5} />}
            label="Precautions" tone="bg-[#00E5FF]" content={element.precautions} borderRight />
          <Section testid="element-detail-firstaid" icon={<ShieldAlert strokeWidth={2.5} />}
            label="First Aid Treatment" tone="bg-[#FF003C] text-white" content={element.firstaid} invert />
        </div>

        {/* Antidotes & Medicines */}
        {antidote && (
          <div className="border-b-2 border-zinc-950 p-6 md:p-8 bg-white" data-testid="element-detail-antidote">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-zinc-950 bg-[#00E5FF] text-zinc-950 font-mono text-[10px] uppercase tracking-[0.25em] font-bold mb-4">
              <Pill size={14} strokeWidth={2.5} />
              Antidotes & Medicines
            </div>
            <p className="text-base leading-relaxed text-zinc-900">{antidote}</p>
            <div className="mt-4 bg-[#FFD700] border-2 border-zinc-950 px-3 py-2 font-mono text-[10px] uppercase tracking-widest inline-block">
              ⚠ Always confirm with a clinical toxicologist or call poison control before administration.
            </div>
          </div>
        )}

        {/* Isotopes */}
        <div className="border-b-2 border-zinc-950 p-6 md:p-8" data-testid="element-detail-isotopes">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-zinc-950 bg-zinc-950 text-white font-mono text-[10px] uppercase tracking-[0.25em] font-bold mb-4">
            <Activity size={14} strokeWidth={2.5} />
            Key Isotopes & Half-Lives
          </div>
          {isotopes.length === 0 ? (
            <div className="font-mono text-sm text-zinc-600">No isotope data available.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-2 border-zinc-950 text-sm">
                <thead className="bg-zinc-950 text-white font-mono uppercase text-[10px] tracking-[0.2em]">
                  <tr>
                    <th className="text-left px-3 py-2 border-r border-zinc-700">Isotope</th>
                    <th className="text-left px-3 py-2 border-r border-zinc-700">Half-Life</th>
                    <th className="text-left px-3 py-2 border-r border-zinc-700">Decay Mode</th>
                    <th className="text-left px-3 py-2">Abundance</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {isotopes.map((iso, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50"}>
                      <td className="px-3 py-2 border-r border-zinc-300 font-bold">{element.s}-{iso.a}</td>
                      <td className="px-3 py-2 border-r border-zinc-300">{iso.half_life}</td>
                      <td className="px-3 py-2 border-r border-zinc-300">{iso.decay}</td>
                      <td className="px-3 py-2">{iso.abundance || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-zinc-50 p-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-zinc-600">
            <Atom size={14} strokeWidth={2.5} />
            <span>Reference data · For laboratory training use only</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => buildElementSheet({ ...element, isotopes, antidote, ...extra })}
              data-testid="element-detail-pdf"
              className="bg-[#FFD700] text-zinc-950 font-mono text-xs uppercase tracking-widest px-5 py-3 border-2 border-zinc-950 hover:neo-shadow hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
            >
              <FileDown size={14} strokeWidth={2.5} /> Safety Sheet PDF
            </button>
            <button
              onClick={() => onOpenChange(false)}
              className="bg-zinc-950 text-white font-mono text-xs uppercase tracking-widest px-6 py-3 border-2 border-zinc-950 hover:bg-zinc-800 transition-colors"
              data-testid="element-detail-close-bottom"
            >
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ icon, label, tone, content, borderRight, invert, testid }) => (
  <div data-testid={testid}
    className={`p-6 md:p-8 ${borderRight ? "md:border-r-2 md:border-zinc-950" : ""} ${invert ? tone : "bg-white"}`}>
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 border-2 border-zinc-950 ${invert ? "bg-white text-zinc-950" : tone} font-mono text-[10px] uppercase tracking-[0.25em] font-bold mb-4`}>
      <span className="w-4 h-4 flex items-center justify-center">{icon}</span>
      {label}
    </div>
    <p className={`text-base leading-relaxed ${invert ? "text-white font-medium" : "text-zinc-900"}`}>
      {content}
    </p>
  </div>
);

const InfoCell = ({ label, value, mono }) => (
  <div className="bg-white p-4">
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-1.5">{label}</div>
    <div className={`text-sm text-zinc-900 leading-snug ${mono ? "font-mono" : ""}`}>{value}</div>
  </div>
);
